'use client';

import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/shared/ui/components/card';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/shared/ui/components/chart';
import { cn } from '@/shared/lib/utils';
import { EXPECTED_ATTENDANCE } from './mockData';

/**
 * Dashboard-preview chart: expected guests per open hour today, as a single bar series. Renamed
 * and stripped down from the shadcn-studio `UserActivityCard` block (dropped its tabs, churn /
 * source datasets, and dropdown). Data is mock (see {@link EXPECTED_ATTENDANCE}).
 */
export function ExpectedAttendanceCard({ className }: { className?: string }) {
  const { t } = useTranslation('common');

  const config = {
    guests: {
      label: t('workspace.dashboard.attendanceGuests'),
      color: 'var(--chart-2)',
    },
  } satisfies ChartConfig;

  return (
    <Card className={cn('gap-4', className)}>
      <CardHeader className="border-b">
        <div className="flex flex-col gap-1">
          <span className="text-lg font-semibold">{t('workspace.dashboard.attendanceTitle')}</span>
          <span className="text-muted-foreground text-sm">
            {t('workspace.dashboard.attendanceSubtitle')}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={config} className="max-h-75 min-h-40 w-full">
          <BarChart accessibilityLayer data={EXPECTED_ATTENDANCE} margin={{ left: -15 }}>
            <CartesianGrid vertical={false} strokeDasharray="4" stroke="var(--border)" />
            <XAxis
              dataKey="hour"
              tickLine={false}
              tickMargin={10}
              minTickGap={16}
              tick={{ fill: 'var(--muted-foreground)' }}
              axisLine={false}
            />
            <YAxis
              tickLine={false}
              tickMargin={8}
              tick={{ fill: 'var(--muted-foreground)' }}
              axisLine={false}
            />
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
            <Bar dataKey="guests" fill="var(--color-guests)" radius={4} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
